import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  model,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { StreamingService } from '../../core/streaming.service';
import { ItemType } from '../quotes/shared/models';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { IConfiguratorPart } from '../quotes/shared/interfaces';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfiguratorService } from '../shared/configurator.service';
import { IActiveAggregate } from './interfaces';

export interface IToolbarEvent {
  button: string;
  disable: boolean;
}

@Component({
  selector: 'app-configurator',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ScrollingModule,
    MatFormFieldModule,
    MatProgressSpinner,
    FormsModule,
  ],
  templateUrl: './configurator.component.html',
  styleUrl: './configurator.component.scss',
})
export class ConfiguratorComponent implements OnInit, AfterViewInit {
  private service: StreamingService = inject(StreamingService);
  private configService: ConfiguratorService = inject(ConfiguratorService);

  parts: any[] = [];
  viewportParts = signal<any[]>([]);
  partGroups = signal<string[]>([]);
  partManufacturers = signal<string[]>([]);

  finished_good_selectable = signal(true);
  nett_price_selectable = signal(true);
  can_paste = signal(true);

  filterGroup = '';
  filterManufacturer = '';
  searchQuery = '';
  queryParts: any[] = [];
  fetching = false;

  missingBuffer = '';
  reset = model(false);

  @Input() showToolbar = true;
  @Input() showFinishedGood = true;
  @Input() showComposite = true;
  @Input() showPartSelector = true;
  @Input() showPartFilter = true;

  @Output() stopComposite: EventEmitter<any> = new EventEmitter();

  toolbarButtonDisabledState: { [type: string]: WritableSignal<boolean> } = {
    nett_price: this.nett_price_selectable,
    finished_good: this.finished_good_selectable,
    paste: this.can_paste,
  };

  constructor() {
    effect(() => {
      if (this.reset()) {
        this.resetConfigurator();
      }

      const isBaseContext = this.configService.isBaseContext();
      this.updateToolbarState(isBaseContext);
    });
  }

  updateToolbarState(isBaseContext: boolean) {
    console.log('Updating toolbar state: ', isBaseContext);
    this.finished_good_selectable.set(isBaseContext);
    this.nett_price_selectable.set(isBaseContext);
    this.can_paste.set(isBaseContext);
  }

  ngOnInit() {
    this.getParts();
  }

  get activeAggregates(): IActiveAggregate[] {
    return this.configService.activeAggregates;
  }

  getAggregateDescription(id: number): string | null {
    return this.configService.getAggregateDescription(id);
  }

  clearall() {
    this.filterGroup = '';
    this.filterManufacturer = '';
    this.searchQuery = '';
    this.viewportParts.set(this.parts);
  }

  ngAfterViewInit(): void {
    window.document
      .getElementById('filters')
      ?.addEventListener('click', (e) => {
        let filteredParts = [];

        const element = e.target as HTMLSpanElement;
        window.document.getElementById(element.id);
        const filters = window.document.getElementsByClassName('filter-text');
        for (let i = 0; i < filters.length; i++) {
          const filter = filters[i];
          filter.removeAttribute('selected');
        }

        element.setAttribute('selected', '');

        if (element.textContent == 'Parts') {
          filteredParts = this.parts.filter((x) => {
            return x.type == 'part';
          });
        }

        if (element.textContent == 'Enclosures') {
          filteredParts = this.parts.filter((x) => {
            return x.part_is_enclosure;
          });
        }

        if (element.textContent == 'Composites') {
          filteredParts = this.parts.filter((x) => {
            return x.type == 'composite';
          });
        }

        if (element.textContent == 'All') {
          this.viewportParts.set(this.parts);
          return;
        }

        this.viewportParts.set(filteredParts);
      });
  }

  search() {
    if (this.searchQuery == '') {
      return;
    }

    this.fetching = true;
    const url = this.service.makeRequest(
      'search?q=' + encodeURIComponent(this.searchQuery),
    );
    const req = new Request(url);
    fetch(req, { credentials: 'include' }).then((response) => {
      const reader = response.body?.getReader();
      var that = this;
      return new ReadableStream({
        start(controller) {
          return readChunk();
          function readChunk(this: any): any {
            return reader?.read().then(({ done, value }) => {
              if (done) {
                controller.close();
                that.viewportParts.set(that.queryParts);
                /*that.parts.map((x) => {
                  if (that.partGroups().indexOf(x.part_group_ref) < 0) {
                    that.partGroups().push(x.part_group_ref);
                  }

                  if (that.partManufacturers().indexOf(x.part_manufacturer) < 0) {
                    that.partManufacturers().push(x.part_manufacturer);
                  }
                });*/
                that.addPartDetails(that.queryParts);
                that.fetching = false;
                return;
              }

              that.queryParts = [];
              const decoder = new TextDecoder();
              const resp = decoder.decode(value);
              const partsChunk = that.appendParts(resp);
              that.queryParts = that.queryParts.concat(partsChunk);
              return readChunk();
            });
          }
        },
      });
    });
  }

  filterByGroup() {
    if (this.filterGroup == '') {
      this.viewportParts.set(this.parts);
      return;
    }

    this.viewportParts.set(
      this.parts.filter((part) => {
        return part.part_group_ref == this.filterGroup;
      }),
    );
  }

  filterByManufacturer() {
    if (this.filterManufacturer == '') {
      this.viewportParts.set(this.parts);
      return;
    }

    this.viewportParts.set(
      this.parts.filter((part) => {
        return part.part_manufacturer == this.filterManufacturer;
      }),
    );
  }

  getParts() {
    this.fetching = true;
    const url = this.service.makeRequest('stream');
    const req = new Request(url);
    fetch(req, { credentials: 'include' }).then((response) => {
      let that = this;
      const reader = response.body?.getReader();
      return new ReadableStream({
        start(controller) {
          return readChunk();
          function readChunk(): any {
            return reader?.read().then(({ done, value }) => {
              if (done) {
                controller.close();
                that.viewportParts.set(that.parts);
                that.parts.map((x) => {
                  if (
                    that
                      .partGroups()
                      .indexOf(
                        that.configService.capitalise(x.part_group_ref),
                      ) < 0
                  ) {
                    that
                      .partGroups()
                      .push(that.configService.capitalise(x.part_group_ref));
                  }

                  if (
                    that
                      .partManufacturers()
                      .indexOf(
                        that.configService.capitalise(x.part_manufacturer),
                      ) < 0
                  ) {
                    that
                      .partManufacturers()
                      .push(that.configService.capitalise(x.part_manufacturer));
                  }

                  x.selectedMarkup = 1 + x.markups[0] / 100 || 1.5;
                });
                that.fetching = false;
                return;
              }

              const decoder = new TextDecoder();
              let resp = decoder.decode(value);
              let partsChunk = that.appendParts(resp);
              if (partsChunk.length) {
                that.parts = that.parts.concat(partsChunk);
              }
              return readChunk();
            });
          }
        },
      });
    });
  }

  private _msgbuffer = '';
  appendParts(parts: any) {
    let container: any[] = [];
    const split_parts = parts.split('\r\n');

    if (split_parts.length > 0) {
      const num_chunks = split_parts.length;

      for (let i = 0; i < num_chunks; i++) {
        this._msgbuffer += split_parts[i];
        try {
          const data = JSON.parse(this._msgbuffer);
          container = container.concat(data);
          this._msgbuffer = '';
        } catch (err) {}
      }
    }

    return container;
  }

  makeFinishedGood(): Partial<IConfiguratorPart> {
    return {
      type: ItemType.FINISHEDGOOD,
      quantity: 1,
      markup: 1,
      parts: [],
      part_is_enclosure: false,
    };
  }

  makeNettPrice(): Partial<IConfiguratorPart> {
    return {
      type: ItemType.NETTPRICE,
      quantity: 1,
      markup: 1,
      part_is_enclosure: false,
    };
  }

  selectPart(_: any, part: Partial<IConfiguratorPart>) {
    if (part.type == ItemType.PART) {
      if (!part.selectedMarkup) {
        if (part.markups && part.markups[0]) {
          part.selectedMarkup = 1 + part.markups[0] / 100;
        } else {
          part.selectedMarkup = 1.5;
        }
      }
    }

    if (part.parts && part.type == ItemType.COMPOSITE) {
      part.parts = this.addPartDetails(part.parts);
    }

    this.configService.set_selected_part(part);
  }

  updateSelectedMarkup(part: any, markup: number) {
    part.selectedMarkup = 1 + markup / 100;
  }

  isPartDisabled(part: IConfiguratorPart) {
    return !this.configService.canAddPart(part);
  }

  isPartTypeDisabled(part_type: ItemType) {
    return !this.configService.canAddPartType(part_type);
  }

  toggleAggEdit(e: MatSlideToggleChange | undefined = undefined) {
    if (e?.checked) {
      console.log('checked');
    } else {
      this.configService.popAggregate();
    }
  }

  resetConfigurator() {
    this.reset.set(false);
  }

  addPartDetails(parts: IConfiguratorPart[]) {
    parts.map((p) => {
      let part = this.parts.find((part: IConfiguratorPart) => {
        return part.id == p.id;
      });

      if (part) {
        p.configurations = part.configurations;
        p.markups = part.markups;
        if (p.markups) {
          p.selectedMarkup = 1 + p.markups[0] / 100 || 1.5;
        }
      }
    });

    return parts;
  }

  onPaste(): void {
    console.log('onPaste triggered.');
    this.configService.pasteItem();
  }
}
